/**
 * usePricingGrid Hook
 * 
 * Manages pricing grid assignment for landing page visitors:
 * - Checks for existing pricing cookie
 * - Validates if stored grid version is still active
 * - Assigns A/B variant based on ratio (if A/B enabled)
 * - Drops permanent cookie for consistent experience
 * - Tracks visits for analytics
 */
import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';

// Cookie name for pricing grid assignment
const PRICING_COOKIE_NAME = 'sr_pricing_grid';
// Cookie expiry: 1 year
const COOKIE_EXPIRY_DAYS = 365;

/**
 * Get a cookie value by name
 */
const getCookie = (name) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    try {
      return JSON.parse(decodeURIComponent(parts.pop().split(';').shift()));
    } catch {
      return null;
    }
  }
  return null;
};

/**
 * Set a cookie with expiry
 */
const setCookie = (name, value, days) => {
  const date = new Date();
  date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
  const expires = `expires=${date.toUTCString()}`;
  document.cookie = `${name}=${encodeURIComponent(JSON.stringify(value))};${expires};path=/;SameSite=Lax`;
};

/**
 * Delete a cookie
 */
const deleteCookie = (name) => {
  document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
};

/**
 * Custom hook for managing pricing grid on landing page
 */
export const usePricingGrid = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pricingData, setPricingData] = useState(null);
  const [assignedVariant, setAssignedVariant] = useState(null);
  const [assignedVersionId, setAssignedVersionId] = useState(null);
  const [plans, setPlans] = useState([]);
  const [visitTracked, setVisitTracked] = useState(false);

  /**
   * Randomly assign variant based on A/B ratio
   * ratio = probability of getting variant A (0.0 - 1.0)
   */
  const assignVariantByRatio = useCallback((ratio) => {
    const random = Math.random();
    return random < ratio ? 'A' : 'B';
  }, []);

  /**
   * Track visit for analytics
   */
  const trackVisit = useCallback(async (variant) => {
    if (visitTracked) return;
    
    try {
      await api.post('/api/admin/plans/analytics/track-visit', null, {
        params: { variant }
      });
      setVisitTracked(true);
    } catch (err) {
      console.warn('Failed to track visit:', err);
    }
  }, [visitTracked]);

  /**
   * Main initialization logic
   */
  const initializePricing = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Check for existing cookie
      const storedData = getCookie(PRICING_COOKIE_NAME);
      const storedVersionId = storedData?.version_id;
      const storedVariant = storedData?.variant;

      // Fetch pricing data from API
      // If we have a stored version, include it so backend can validate
      const queryParams = storedVersionId ? `?version_id=${storedVersionId}` : '';
      const response = await api.get(`/api/admin/plans/public/pricing${queryParams}`);
      const data = response.data;
      
      setPricingData(data);

      let finalVariant = null;
      let finalVersionId = null;
      let needsNewCookie = false;

      // Case 1: A/B testing is DISABLED
      if (!data.ab_enabled) {
        // Always use variant A when A/B is off
        if (data.variant_a) {
          finalVariant = 'A';
          finalVersionId = data.variant_a.version_id;
          
          // If stored variant was B or different version, need new cookie
          if (storedVariant !== 'A' || storedVersionId !== finalVersionId) {
            needsNewCookie = true;
          }
        }
      }
      // Case 2: A/B testing is ENABLED
      else {
        // Check if stored version is still valid
        if (storedVersionId && data.stored_version_valid && storedVariant) {
          // User's stored version is still active - keep it
          finalVariant = storedVariant;
          finalVersionId = storedVersionId;
        } else {
          // Need to assign new variant (no cookie, invalid cookie, or version changed)
          const abRatio = data.ab_ratio || 0.5;
          finalVariant = assignVariantByRatio(abRatio);
          
          const variantData = finalVariant === 'A' ? data.variant_a : data.variant_b;
          if (variantData) {
            finalVersionId = variantData.version_id;
            needsNewCookie = true;
          } else {
            // Fallback to variant A if assigned variant has no data
            finalVariant = 'A';
            finalVersionId = data.variant_a?.version_id;
            needsNewCookie = true;
          }
        }
      }

      // Update cookie if needed
      if (needsNewCookie && finalVersionId) {
        setCookie(PRICING_COOKIE_NAME, {
          variant: finalVariant,
          version_id: finalVersionId,
          assigned_at: new Date().toISOString()
        }, COOKIE_EXPIRY_DAYS);
      }

      // Set the final state
      setAssignedVariant(finalVariant);
      setAssignedVersionId(finalVersionId);

      // Get plans for the assigned variant
      const variantData = finalVariant === 'A' ? data.variant_a : data.variant_b;
      setPlans(variantData?.plans || []);

      // Track the visit
      if (finalVariant) {
        trackVisit(finalVariant);
      }

    } catch (err) {
      console.error('Failed to initialize pricing:', err);
      setError(err.message || 'Failed to load pricing');
    } finally {
      setLoading(false);
    }
  }, [assignVariantByRatio, trackVisit]);

  // Initialize on mount
  useEffect(() => {
    initializePricing();
  }, [initializePricing]);

  /**
   * Get pricing info for signup flow
   * Returns data needed when user clicks "Sign Up"
   */
  const getSignupPricingInfo = useCallback(() => {
    return {
      variant: assignedVariant,
      version_id: assignedVersionId,
      plans: plans
    };
  }, [assignedVariant, assignedVersionId, plans]);

  /**
   * Get a specific plan by ID or type
   */
  const getPlan = useCallback((planIdOrType) => {
    return plans.find(p => 
      p.plan_id === planIdOrType || 
      p.plan_type === planIdOrType
    );
  }, [plans]);

  /**
   * Get the free plan
   */
  const getFreePlan = useCallback(() => {
    return plans.find(p => p.plan_type === 'free');
  }, [plans]);

  /**
   * Get all paid plans (non-free, non-inquire)
   */
  const getPaidPlans = useCallback(() => {
    return plans.filter(p => 
      p.plan_type !== 'free' && 
      p.plan_type !== 'inquire'
    );
  }, [plans]);

  /**
   * Get enterprise/inquire plan if available
   */
  const getEnterprisePlan = useCallback(() => {
    return plans.find(p => p.plan_type === 'inquire');
  }, [plans]);

  /**
   * Force refresh (clears cookie and re-initializes)
   */
  const forceRefresh = useCallback(() => {
    deleteCookie(PRICING_COOKIE_NAME);
    setVisitTracked(false);
    initializePricing();
  }, [initializePricing]);

  return {
    // State
    loading,
    error,
    plans,
    assignedVariant,
    assignedVersionId,
    pricingData,
    
    // Helpers
    getPlan,
    getFreePlan,
    getPaidPlans,
    getEnterprisePlan,
    getSignupPricingInfo,
    
    // Actions
    forceRefresh,
    trackVisit
  };
};

export default usePricingGrid;
